import moment from "moment";

// Flow
// Recovery configSchedule with timezone of schedule when create schedule
// Parser schedule to slots
// Change slot to new timezone


/**
 * Creates an instance of the ScheduleParser Function.
 *
 * @param {Object} configScheduleDB - The initial configuration for the schedule, including start date, end date, and timezone information.
 * @param {Object} [params={}] - Optional parameters to further configure the schedule.
 * @param {number} [params.currentTimeZone=0] - The current timezone of the user. Used to adjust schedule times based on the user's timezone.
 * @param {number} [params.configScheduleTimezone=0] - The timezone of the schedule. Used to adjust schedule times based on the schedule's original timezone.
 * @param {string} [params.startDate=""] - The start date of the schedule in "MM/DD/YYYY" format.
 * @param {string} [params.endDate=""] - The end date of the schedule in "MM/DD/YYYY" format. Used to calculate the maximum date for the schedule.
 */
const ScheduleParser = (configScheduleDB, params = {
    currentTimeZone: 0,
    configScheduleTimezone: 0,
    startDate: "",
    endDate: "",
}) => {
    const DEFAULT_CONFIG = {
        DATE_FORMAT: "MM/DD/YYYY",
        TIME_FORMAT: "HH:mm",
        TIME_FORMAT_12: "hh:mm A",
        DATE_TIME_FORMAT: "MM/DD/YYYY HH:mm",
    };
    let configSchedule = configScheduleDB || {};
    const currentTimeZone = params?.currentTimeZone || 0; // Current timezone of user
    const configScheduleTimezone = params?.configScheduleTimezone || 0; // Timezone of schedule
    const createdTimezone = configSchedule?.tzCreated || 0; // Timezone of schedule when create schedule
    const maxDate = params?.endDate && moment(params?.endDate, DEFAULT_CONFIG.DATE_FORMAT).isValid() ? moment(params?.endDate, DEFAULT_CONFIG.DATE_FORMAT) : null;
    // reset configSchedule repeatOn if not exist
    if (!configSchedule?.repeatOn?.length) {
        configSchedule.repeatOn = [];
    }

    // Recovery config schedule with timezone of schedule when create schedule
    const recoveryConfigScheduleWithTz = (configS) => {
        const {startDate, startTime} = configS;
        const start = getMomentFromText(configS);
        const diffHour = createdTimezone - configScheduleTimezone;
        const currentRepeatOn = configS.repeatOn || [];
        // Recovery repeatOn with new timezone (current no change because repeatOn is day of week not change in DB)
        const repeatOn = currentRepeatOn;

        return {
            ...configS,
            startDate: start.clone().add(diffHour, "hours").format(DEFAULT_CONFIG.DATE_FORMAT),
            startTime: start.clone().add(diffHour, "hours").format(DEFAULT_CONFIG.TIME_FORMAT),
            events: (configS.events || []).map(event => {
                const eventStart = getMomentFromText(event);
                const eventNewStart = getMomentFromText({
                    startDate: event?.newStartDate || event.startDate,
                    startTime: event?.newStartTime || event.startTime
                });

                return {
                    ...event,
                    startDate: eventStart.clone().add(diffHour, "hours").format(DEFAULT_CONFIG.DATE_FORMAT),
                    startTime: eventStart.clone().add(diffHour, "hours").format(DEFAULT_CONFIG.TIME_FORMAT),
                    newStartDate: event.newStartDate ? eventNewStart.clone().add(diffHour, "hours").format(DEFAULT_CONFIG.DATE_FORMAT) : undefined,
                    newStartTime: event.newStartTime ? eventNewStart.clone().add(diffHour, "hours").format(DEFAULT_CONFIG.TIME_FORMAT) : undefined,
                }
            }),
            repeatOn,
        }
    }

    const getMomentFromText = (params) => {
        const {startDate, startTime} = params;
        const current = moment(`${startDate} ${startTime}`, DEFAULT_CONFIG.DATE_TIME_FORMAT);
        return current.clone();
    }

    const getLocalDateId = (momentInstance) => {
        return parseInt(momentInstance.format("YYYYMMDD"));
    }

    const parser = (findFirstSlot = null) => {
        const {slot, startDate, startTime, slotDuration, repeatEvery, repeatUnit, repeatOn} = configSchedule;
        let slots = [];
        const start = getMomentFromText(configSchedule);
        // Calculate end date
        let end = start.clone().add(repeatEvery, repeatUnit).endOf("day");
        // check max date
        if (!!maxDate && maxDate.toDate().getTime() < end.toDate().getTime()) {
            end = maxDate;
        }
        // Cap at 6 months from today
        const maxFutureDate = moment().add(6, 'months').endOf("day");
        if (end.toDate().getTime() > maxFutureDate.toDate().getTime()) {
            end = maxFutureDate;
        }

        // Loop from start to end date
        let step = start.clone();
        while (step.isBefore(end)) {
            let slotStart = step.clone();
            const dayOfWeek = slotStart.day();
            // Check day is available in repeatOn
            if (repeatOn.includes(dayOfWeek) || slotStart.isSame(start, "date")) {
                // Create slot in days
                for (let i = 0; i < slot; i++) {
                    const slotEnd = slotStart.clone().add(slotDuration, "seconds");
                    // Create slot data
                    let slotData = {
                        schedule: configSchedule,
                        regions: configSchedule?.regions || [],
                        locationType: configSchedule?.locationType,
                        start: slotStart.clone(),
                        slotIndex: i,
                        paxAvaiable: configSchedule.paxPerSlot || 0,
                        paxAvailable: configSchedule.paxPerSlot || 0,
                        paxPerSlot: configSchedule.paxPerSlot || 0,
                        isBooked: configSchedule.isBooked || false,
                        isBookedSchedule: configSchedule.isBooked || false,
                        isBookedItem: false,
                        slotDuration: slotDuration,
                        slotId: undefined,
                    };
                    // Add slot origin
                    slotData.slotOrigin = JSON.parse(JSON.stringify(slotData));
                    slotData.slotOrigin.start = slotData.start.clone();
                    delete slotData.slotOrigin.schedule;
                    // Find configs for this slot
                    const slotEvents = (configSchedule.events || []).filter(event => !event?.isOtherSlot
                        && event.startDate === slotData.start.format(DEFAULT_CONFIG.DATE_FORMAT)
                        && event.startTime === slotData.start.format(DEFAULT_CONFIG.TIME_FORMAT)
                    );
                    // Add events to slot
                    slotData.events = slotEvents;

                    // Apply event to slot
                    slotEvents.forEach(event => {
                        const eventStart = getMomentFromText({
                            startDate: event?.newStartDate || event.startDate,
                            startTime: event?.newStartTime || event.startTime,
                        });
                        const eventEnd = eventStart.clone().add(event.slotDuration || configSchedule.slotDuration, "seconds");
                        // set event not add if one of event is deleted
                        if (event.status === 'deleted') {
                            slotData.isDeleted = true;
                        }
                        // Count pax available for slot from event paxAvailable or default paxPerSlot of schedule
                        let paxAvailable = (event.paxAvaiable >= 0 ? event.paxAvaiable : (event.paxPerSlot || configSchedule.paxPerSlot || 0));

                        // Update slot data
                        slotData = {
                            ...slotData,
                            ...event,
                            start: eventStart.clone(),
                            paxAvaiable: paxAvailable,
                            paxAvailable: paxAvailable,
                            paxPerSlot: event.paxPerSlot || configSchedule.paxPerSlot,
                            isBooked: slotData.isBooked || false,
                            isBookedSchedule: slotData.isBookedSchedule || false,
                            isBookedItem: event.isBooked || false,
                            slotDuration: event.slotDuration || configSchedule.slotDuration,
                            slotId: event.slotId || undefined,
                            eventApplied: {...(slotData?.event || {}), ...event},
                        }
                    });
                    // Add slot to slots list if not deleted
                    if (!slotData.isDeleted) {
                        slots.push(slotData);
                        // Return first slot if findFirstSlot is true and slot is available
                        if (!!findFirstSlot) {
                            let checked = true;

                            // Check the slot date, if it is in the past, return false
                            let slotDataStart = slotData.start.clone();
                            const currentDate = moment();
                            const systemTimezone = moment().utcOffset() / 60; // get system timezone
                            const diffHourTimezone = currentTimeZone - systemTimezone;
                            slotDataStart = slotDataStart.add(diffHourTimezone, "hours").clone(); // convert slot to system timezone
                            if (slotDataStart.isBefore(currentDate)) { // check slot date is before current date
                                checked = false;
                            }

                            // check slot with slot available
                            if (findFirstSlot.slotAvailable && slotData.paxAvailable <= 0) {
                                checked = false;
                            }
                            // check slot with postal code
                            if (findFirstSlot.postalCode && !checkRegionAvailable(slotData.regions, findFirstSlot.postalCode)) {
                                checked = false;
                            }
                            // check slot with date ranges
                            if (findFirstSlot.dateRanges && findFirstSlot.dateRanges.length > 0) {
                                // check slot with date ranges is out of range of slot date or not
                                const clientStartTz = slotData.start.clone().add(currentTimeZone - createdTimezone, "hours"); // convert slot to client timezone
                                const outRange = !!findFirstSlot.dateRanges.find(x => x.startDate.isSameOrAfter(clientStartTz) || x.endDate.isSameOrBefore(clientStartTz));
                                if (outRange) {
                                    checked = false;
                                }
                            }

                            if (checked) {
                                return [slotData];
                            }
                        }
                    }
                    slotStart = slotStart.add(slotDuration, "seconds");
                }
            }

            step = step.add(1, 'day').clone();
        }
        // Return first slot if findFirstSlot is true and slot is not available
        if (!!findFirstSlot) {
            return null;
        }
        // Add other slot
        const otherEvents = (configSchedule.events || []).filter(event => event?.isOtherSlot);
        otherEvents.map(event => {
            const slotStart = getMomentFromText({
                startDate: event?.startDate,
                startTime: event?.startTime,
            });
            const slotEnd = slotStart.clone().add(event.slotDuration || configSchedule.slotDuration, "seconds");
            let slotDataReschedule = {
                schedule: configSchedule,
                start: slotStart.clone(),
                slotIndex: 0,
                paxAvaiable: event.paxAvaiable || 0,
                paxAvailable: event.paxAvaiable || 0,
                paxPerSlot: event.paxPerSlot || 0,
                isBooked: event.isBooked || false,
                isBookedSchedule: event.isBooked || false,
                isBookedItem: false,
                slotDuration: slotDuration,
                dayOfWeek: slotStart.day(),
                slotId: event.slotId || undefined,
                events: [event],
                isOtherSlot: true,
                isReschedule: true,
                eventApplied: event
            };
            slotDataReschedule.slotOrigin = JSON.parse(JSON.stringify(slotDataReschedule));
            slotDataReschedule.slotOrigin.start = slotDataReschedule.start.clone();
            delete slotDataReschedule.slotOrigin.schedule;
            slots.push(slotDataReschedule);
        });


        return slots;
    }

    const checkRegionAvailable = (regions, postalCode) => {
        const firstPostalCode = (postalCode || '')?.toString().substring(0, 2);
        return regions.find(item => item.postalCodes?.includes(firstPostalCode));
    }

    // Convert slots to new timezone
    const convertSlotsToNewTimezone = (slots) => {
        // Update slot to new timezone
        const diffHourTimezone = currentTimeZone - createdTimezone;
        slots = slots.map(slot => {
            // Update schedule time to new timezone
            const slotScheduleStart = getMomentFromText(slot.schedule);
            slot.schedule = {
                ...slot.schedule,
                startDate: slotScheduleStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT),
                startTime: slotScheduleStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT),
                events: (slot.schedule.events || []).map(event => {
                    const eventStart = getMomentFromText(event);
                    const eventNewStart = getMomentFromText({
                        startDate: event?.newStartDate || event.startDate,
                        startTime: event?.newStartTime || event.startTime
                    });

                    return {
                        ...event,
                        startDate: eventStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT),
                        startTime: eventStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT),
                        newStartDate: event.newStartDate ? eventNewStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT) : undefined,
                        newStartTime: event.newStartTime ? eventNewStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT) : undefined,
                    }
                }),
            }
            // Update slotOrigin time to new timezone
            slot.slotOrigin.start = slot.slotOrigin.start.clone().add(diffHourTimezone, "hours");
            slot.slotOrigin.end = slot.slotOrigin.start.clone().add(slot.slotDuration, "seconds");
            // Update eventApplied time to new timezone
            if (slot.eventApplied) {
                const eventAppliedStart = getMomentFromText(slot.eventApplied);
                const eventAppliedNewStart = getMomentFromText({
                    startDate: slot?.eventApplied.newStartDate || slot.eventApplied.startDate,
                    startTime: slot?.eventApplied.newStartTime || slot.eventApplied.startTime,
                });
                slot.eventApplied = {
                    ...slot.eventApplied,
                    startDate: eventAppliedStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT),
                    startTime: eventAppliedStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT),
                    newStartDate: slot.eventApplied.newStartDate ? eventAppliedNewStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT) : undefined,
                    newStartTime: slot.eventApplied.newStartTime ? eventAppliedNewStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT) : undefined
                }
            }
            // Update event time to new timezone
            slot.events = slot.events.map(event => {
                const eventStart = getMomentFromText(event);
                const eventNewStart = getMomentFromText({
                    startDate: event?.newStartDate || event.startDate,
                    startTime: event?.newStartTime || event.startTime
                });

                return {
                    ...event,
                    startDate: eventStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT),
                    startTime: eventStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT),
                    newStartDate: event.newStartDate ? eventNewStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.DATE_FORMAT) : undefined,
                    newStartTime: event.newStartTime ? eventNewStart.clone().add(diffHourTimezone, "hours").format(DEFAULT_CONFIG.TIME_FORMAT) : undefined,
                }
            });
            // Update slot time to new timezone
            slot.start = slot.start.clone().add(diffHourTimezone, "hours");
            slot.end = slot.start.clone().add(slot.slotDuration, "seconds");
            slot.startTime = slot.start.format(DEFAULT_CONFIG.TIME_FORMAT);
            slot.startDate = slot.start.format(DEFAULT_CONFIG.DATE_FORMAT);
            delete slot.newStartTime;
            delete slot.newStartDate;
            return slot;
        });
        return slots;
    }

    // Get slots with new format
    const getSlots = () => {
        let slots = parser();
        slots = convertSlotsToNewTimezone(slots);
        return slots;
    }

    // Get slots with legacy format
    const getLegacySlots = () => {
        let slots = parser();
        slots = convertSlotsToNewTimezone(slots);
        return slots.map(slot => {
            return {
                ...slot.schedule,
                ...slot,
                sid: slot.schedule.id,
                scheduleId: slot.schedule.id,
                isBookedSchedule: slot?.isBooked || false,
                originStartDate: slot.slotOrigin.start.format(DEFAULT_CONFIG.DATE_FORMAT),
                fixedDay: slot.start.day(),
                startDate: slot.start.format(DEFAULT_CONFIG.DATE_FORMAT),
                endDate: slot.end.format(DEFAULT_CONFIG.DATE_FORMAT),
                startTime: slot.slotOrigin.start.format(DEFAULT_CONFIG.TIME_FORMAT),
                origin: {
                    ...slot.schedule,
                    ...slot.slotOrigin,
                    startDate: slot.start.format(DEFAULT_CONFIG.DATE_FORMAT),
                    startTime: slot.slotOrigin.start.format(DEFAULT_CONFIG.TIME_FORMAT),
                    value: {
                        ...slot.slotOrigin.value,
                        id: Math.floor(parseFloat(slot.slotOrigin.start.format("HH.mm"))),
                        index: (slot.slotOrigin.slotIndex + 1),
                        fromTime: slot.slotOrigin.start,
                        toTime: slot.slotOrigin.end,
                        fromTimeText: slot.slotOrigin.start.format("HH:mm"),
                        toTimeText: slot.slotOrigin.end.format("HH:mm"),
                        fromTimeText2: slot.slotOrigin.start.format("hh:mm A"),
                        toTimeText2: slot.slotOrigin.end.format("hh:mm A"),
                    }
                },
                id: getLocalDateId(slot.start),
                value: {
                    ...slot.value,
                    id: Math.floor(parseFloat(slot.start.format("HH.mm"))),
                    index: (slot.slotIndex + 1),
                    fromTime: slot.start,
                    toTime: slot.end,
                    fromTimeText: slot.start.format("HH:mm"),
                    toTimeText: slot.end.format("HH:mm"),
                    fromTimeText2: slot.start.format("hh:mm A"),
                    toTimeText2: slot.end.format("hh:mm A"),
                }
            }
        });
    }

    const getFirstSlotAvailable = (params = {
        postalCode: "",
        slotAvailable: false,
        dateRanges: [],
    }) => {
        let slots = parser(params);
        slots = convertSlotsToNewTimezone(slots || []);
        return slots.length > 0 ? {
            startDate: slots[0].start.toDate().toISOString(),
            startDateFormat: slots[0].start.format(DEFAULT_CONFIG.DATE_FORMAT),
            startDateTimeFormat: slots[0].start.format(DEFAULT_CONFIG.DATE_TIME_FORMAT),
            schedule: slots[0].schedule,
            slot: slots[0],
        } : null;
    }


    if (createdTimezone !== configScheduleTimezone) {
        configSchedule = recoveryConfigScheduleWithTz(configSchedule);
    }

    return {
        getSlots,
        getLegacySlots,
        getFirstSlotAvailable,
    }
}


export default ScheduleParser;